/**
 * WB4.1 P79-6 — sellable golden slice canonical action ids (top 5 + bulk min 1).
 * WB4.5 AI lane — suggest API pilot ids: `ai-suggest-pilot-registry.ts` (same ids as palette).
 */

import { ROLES } from '@shared/types/roles';
import { BULK_STATE_CHANGE_HANDLER_REFS } from './workbench-bulk-handler-refs';
import type { ActionRuleEntry, WorkbenchAction } from './action-types';
import type { WorkbenchActionSafety } from './workbench-action-safety-policy';
import { buildActionSafety } from './workbench-action-safety-policy';

/** Canonical sellable top-5 action ids — palette · inspector · context menu SSOT. */
export const SELLABLE_TOP_5_ACTION_IDS = [
  'open_detail',
  'start_order',
  'start_inventory_workflow',
  'edit_description',
  'view_audit',
] as const;

export type SellableTop5ActionId = (typeof SELLABLE_TOP_5_ACTION_IDS)[number];

export const SELLABLE_BULK_ACTION_IDS = [
  'view_audit_bulk',
  'bulk_activate',
  'bulk_archive',
] as const;

export const SELLABLE_GOLDEN_HANDLER_REFS = {
  openDetail: 'workbench:open-detail',
  editDescription: 'workbench:edit-description',
  viewAudit: 'workbench:view-audit',
  viewAuditBulk: 'workbench:view-audit-bulk',
  startOrder: 'flow:start:order_intake',
  startInventoryWorkflow: 'flow:start:item.inventory',
} as const;

type SellableActionSeed = Omit<WorkbenchAction, 'placement'> & {
  safety: WorkbenchActionSafety;
};

function sellableAction(
  seed: Omit<SellableActionSeed, 'safety' | 'riskTier'> & {
    riskTier: 'low' | 'medium' | 'high' | 'finance';
  },
  overrides?: Partial<WorkbenchActionSafety>
): SellableActionSeed {
  const { riskTier, surfaces, ...rest } = seed;
  const safety = buildActionSafety(riskTier, surfaces, overrides);
  return { ...rest, surfaces, safety, riskTier: safety.riskTier };
}

export const SELLABLE_GOLDEN_ACTION_RULE_TABLE: readonly ActionRuleEntry[] = [
  {
    condition: {
      domains: ['sellable_item'],
      roles: '*',
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      sellableAction({
        id: 'open_detail',
        label: '상세 열기',
        priority: 100,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.openDetail,
        group: 'open',
        permissionKey: 'read',
        riskTier: 'low',
      }),
      sellableAction({
        id: 'start_order',
        label: '주문 시작',
        priority: 88,
        surfaces: ['table-row', 'inspector', 'command'],
        handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.startOrder,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'high',
      }),
      sellableAction({
        id: 'start_inventory_workflow',
        label: '재고 워크플로우',
        priority: 86,
        surfaces: ['table-row', 'inspector', 'command'],
        handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.startInventoryWorkflow,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'high',
      }),
      sellableAction(
        {
          id: 'edit_description',
          label: '설명 편집',
          priority: 84,
          surfaces: ['table-row', 'inspector', 'command'],
          handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.editDescription,
          group: 'ai_suggested',
          permissionKey: 'update',
          riskTier: 'low',
        },
        {
          recoverabilityMode: 'toast_undo',
        }
      ),
      sellableAction({
        id: 'view_audit',
        label: '감사 기록',
        priority: 80,
        surfaces: ['table-row', 'inspector', 'command'],
        handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.viewAudit,
        group: 'admin',
        permissionKey: 'read',
        riskTier: 'low',
      }),
      sellableAction({
        id: 'view_audit_bulk',
        label: '감사 기록 (선택)',
        priority: 55,
        surfaces: ['table-row'],
        handlerRef: SELLABLE_GOLDEN_HANDLER_REFS.viewAuditBulk,
        group: 'state_change',
        permissionKey: 'read',
        riskTier: 'low',
      }),
      sellableAction({
        id: 'bulk_activate',
        label: '활성화 (선택)',
        priority: 52,
        surfaces: ['table-row'],
        handlerRef: BULK_STATE_CHANGE_HANDLER_REFS.activate,
        group: 'state_change',
        permissionKey: 'update',
        riskTier: 'medium',
      }),
      sellableAction({
        id: 'bulk_archive',
        label: '보관 (선택)',
        priority: 51,
        surfaces: ['table-row'],
        handlerRef: BULK_STATE_CHANGE_HANDLER_REFS.archive,
        group: 'state_change',
        permissionKey: 'update',
        riskTier: 'medium',
      }),
    ],
  },
  {
    condition: {
      domains: ['sellable_item'],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER],
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      sellableAction({
        id: 'delete',
        label: '삭제',
        priority: 10,
        surfaces: ['table-row', 'detail-header', 'command'],
        handlerRef: 'crud:delete',
        permissionKey: 'delete',
        riskTier: 'high',
      }),
    ],
  },
] as const;
